import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFormState } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../../util/reactIntl';

import {
  OutsideClickHandler,
  IconDate,
  FieldSelect,
  FieldSingleDateController,
} from '../../../../../components';

import css from './FilterDateTime.module.css';

// Selectable times, on the hour. The last start option is 23:00 so that a booking always
// has at least one later end option to pair with.
const MINUTES_STEP = 60;

/**
 * Build the list of selectable clock times as { value: 'HH:mm', label: 'HH:mm' }.
 *
 * @param {number} fromMinutes - first selectable time, in minutes from midnight
 * @returns {Array<{value: string, label: string}>}
 */
export const getTimeOptions = (fromMinutes = 0) => {
  const options = [];
  for (let minutes = fromMinutes; minutes < 24 * 60; minutes += MINUTES_STEP) {
    const hh = `${Math.floor(minutes / 60)}`.padStart(2, '0');
    const mm = `${minutes % 60}`.padStart(2, '0');
    options.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
  }
  return options;
};

/** 'HH:mm' as minutes from midnight, or null when unparseable. */
export const parseTimeToMinutes = time => {
  const match = typeof time === 'string' ? time.match(/^(\d{2}):(\d{2})$/) : null;
  if (!match) {
    return null;
  }
  return Number.parseInt(match[1], 10) * 60 + Number.parseInt(match[2], 10);
};

const handleKeyDown = (isOpen, setIsOpen) => e => {
  const toggleButton = e.currentTarget.getElementsByClassName(css.toggleButton)[0];
  if ((e.target === toggleButton && e.key === 'Enter') || e.key === ' ') {
    e.preventDefault();
    setIsOpen(prevState => !prevState);
    return;
  } else if (!isOpen && e.key === 'ArrowDown') {
    e.preventDefault();
    setIsOpen(true);
    return;
  } else if (isOpen && e.key === 'Escape') {
    e.preventDefault();
    toggleButton.focus();
    setIsOpen(false);
    return;
  }
};

/**
 * FilterDateTime lets the visitor pick one day and a start and end time on that day.
 *
 * It replaces the earlier date-range filter: for an hourly marketplace, "which day and
 * between which hours" is the real question, and a two-date range could not express it.
 *
 * The form values it owns are `date` (a Date), `startTime` and `endTime` (both 'HH:mm').
 * SearchCTA turns them into the `dates` and `times` query parameters.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.config - Marketplace configuration object
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} [props.alignLeft] - Whether the dropdown aligns to the left edge
 * @returns {JSX.Element}
 */
const FilterDateTime = props => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleButtonRef = useRef(null);
  const { className, rootClassName, alignLeft } = props;
  const intl = useIntl();

  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const { date, startTime, endTime } = values || {};

  useEffect(() => {
    if (FieldSingleDateController.preload) {
      FieldSingleDateController.preload();
    }
  }, []);

  const classes = classNames(rootClassName || css.root, className);

  const startMinutes = parseTimeToMinutes(startTime);
  const startOptions = getTimeOptions(0).slice(0, -1);
  // An end time must be after the chosen start, so the options begin one step later.
  const endOptions = getTimeOptions(
    startMinutes != null ? startMinutes + MINUTES_STEP : MINUTES_STEP
  );

  // Clear an end time that the new start time has invalidated.
  const handleStartTimeChange = value => {
    const newStart = parseTimeToMinutes(value);
    const currentEnd = parseTimeToMinutes(endTime);
    if (newStart != null && currentEnd != null && currentEnd <= newStart) {
      form.change('endTime', undefined);
    }
  };

  // Picking the end time completes the selection, so close up and hand focus back to the
  // toggle button, the same way choosing a date used to close the old range picker.
  const handleEndTimeChange = value => {
    if (value) {
      toggleButtonRef.current?.focus();
      setIsOpen(false);
    }
  };

  const handleClick = event => {
    const el = event.currentTarget;
    const dropdownHeight = 460; // approximately: calendar plus the two time selects
    const toBottom = window.innerHeight - el.getBoundingClientRect().bottom;
    // If there's not enough space under the toggle button, scroll down to make space for the dropdown.
    if (!isOpen && toBottom < dropdownHeight) {
      const topbarOffset = 72;
      const toTop = el.getBoundingClientRect().top - topbarOffset;
      const scrollDownNeed = dropdownHeight - toBottom;
      const top = toTop < scrollDownNeed ? toTop : scrollDownNeed;
      window.scrollBy({ top });
    }
    setIsOpen(prevState => !prevState);
  };

  const formattedDate = date ? intl.formatDate(date, { day: 'numeric', month: 'short' }) : null;
  const label =
    formattedDate && startTime && endTime
      ? `${formattedDate}, ${startTime}–${endTime}`
      : formattedDate
      ? formattedDate
      : intl.formatMessage({ id: 'PageBuilder.SearchCTA.dateFilterPlaceholder' });

  const labelClasses = classNames(css.label, {
    [css.active]: date || isOpen,
  });

  return (
    <OutsideClickHandler
      className={classes}
      onOutsideClick={() => setIsOpen(false)}
      onKeyDown={handleKeyDown(isOpen, setIsOpen)}
    >
      <div
        role="button"
        ref={toggleButtonRef}
        className={css.toggleButton}
        onClick={handleClick}
        tabIndex={0}
        aria-controls={isOpen ? 'dateTime' : ''}
        aria-expanded={isOpen}
      >
        <IconDate rootClassName={css.iconDate} />
        <span className={labelClasses}>{label}</span>
      </div>

      {isOpen ? (
        <div className={classNames(css.dropdown, { [css.alignLeft]: alignLeft })} id="dateTime">
          <FieldSingleDateController className={css.datePicker} name="date" id="date" />

          <div className={css.times}>
            <FieldSelect
              className={classNames(css.timeField, { [css.timeFieldActive]: !!startTime })}
              id="startTime"
              name="startTime"
              label={intl.formatMessage({ id: 'PageBuilder.SearchCTA.startTimeLabel' })}
              onChange={handleStartTimeChange}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'PageBuilder.SearchCTA.timePlaceholder' })}
              </option>
              {startOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </FieldSelect>

            <FieldSelect
              className={classNames(css.timeField, { [css.timeFieldActive]: !!endTime })}
              id="endTime"
              name="endTime"
              label={intl.formatMessage({ id: 'PageBuilder.SearchCTA.endTimeLabel' })}
              // An end time is meaningless before a start time has been chosen.
              disabled={startMinutes == null}
              // The end time is the last thing to pick, so the popup has done its job.
              onChange={handleEndTimeChange}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'PageBuilder.SearchCTA.timePlaceholder' })}
              </option>
              {endOptions.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </FieldSelect>
          </div>
        </div>
      ) : null}
    </OutsideClickHandler>
  );
};

export default FilterDateTime;
